import React, { useState, useEffect, useRef } from 'react';
import {
    Box, VStack, HStack, Heading, Text, Button, Input, FormControl, FormLabel,
    SimpleGrid, Badge, Divider, useToast, Flex, IconButton,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
    useDisclosure, Table, Thead, Tbody, Tr, Th, Td, Select, Spinner,
    Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerCloseButton,
    Tabs, TabList, Tab, TabPanels, TabPanel,
} from '@chakra-ui/react';
import { FiPlus, FiRefreshCw, FiUsers, FiMonitor, FiKey, FiMessageCircle, FiMail, FiEye, FiFileText } from 'react-icons/fi';
import axios from 'axios';
import CompanyManagementSection from '../Components/PlatformAdmin/CompanyManagementSection';
import SmsUsageSection from '../Components/PlatformAdmin/SmsUsageSection';
import ChatUsageSection from '../Components/PlatformAdmin/ChatUsageSection';
import VisionUsageSection from '../Components/PlatformAdmin/VisionUsageSection';
import CustomerPhoneSection from '../Components/PlatformAdmin/CustomerPhoneSection';
import DirectoryOutsideSection from '../Components/PlatformAdmin/DirectoryOutsideSection';
import BrandInsightsSection from '../Components/PlatformAdmin/BrandInsightsSection';
import PlatformInvoiceModal, { previousMonth } from '../Components/PlatformAdmin/PlatformInvoiceModal';
import TenantMailModal from '../Components/PlatformAdmin/TenantMailModal';

const API_URL = process.env.REACT_APP_AWS_API_URL || '';
const OPERATORS_FB_GROUP = 'https://www.facebook.com/groups/1141717998392158';

function asUtcInstant(value) {
    if (!value) return '';
    let raw = String(value).trim().replace(' ', 'T');
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(raw) && !/(?:Z|[+-]\d{2}:?\d{2})$/i.test(raw)) raw += 'Z';
    return raw;
}

function formatCentral(value, withDate) {
    const date = new Date(asUtcInstant(value));
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleString('en-US', {
        timeZone: 'America/Chicago',
        hour: '2-digit',
        minute: '2-digit',
        ...(withDate ? { month: 'short', day: 'numeric' } : {}),
    });
}

function ChatComposer({ inputRef, placeholder, colorScheme, onSend, isSending }) {
    const [text, setText] = useState('');
    const send = async () => {
        const value = text.trim();
        if (!value || isSending) return;
        setText('');
        const ok = await onSend(value);
        if (ok === false) setText(value);
    };
    return (
        <HStack flexShrink={0} pt={2} borderTop="1px solid" borderColor="gray.200">
            <Input
                ref={inputRef}
                placeholder={placeholder}
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); send(); } }}
                size="md"
                fontSize="16px"
                enterKeyHint="send"
            />
            <Button colorScheme={colorScheme} size="sm" onClick={send} isLoading={isSending} px={5}>Send</Button>
        </HStack>
    );
}

function mergeServerMessages(prev, next) {
    const pending = prev.filter(m => String(m.messageId).startsWith('local-'));
    const unmatched = pending.filter(p => !next.some(n => n.senderType === p.senderType && n.message === p.message));
    const merged = unmatched.length ? [...next, ...unmatched] : next;
    if (prev.length === merged.length && prev.every((m, i) => m.messageId === merged[i].messageId && m.message === merged[i].message)) {
        return prev;
    }
    return merged;
}

export default function PlatformAdminPage() {
    const [platformKey, setPlatformKey] = useState(localStorage.getItem('platformKey') || '');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [laundries, setLaundries] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedLaundry, setSelectedLaundry] = useState(null);
    const { isOpen, onOpen, onClose } = useDisclosure();
    const toast = useToast();
    const [tabIndex, setTabIndex] = useState(() => parseInt(localStorage.getItem('platformAdminTab') || '0'));
    const [laundryTabIndex, setLaundryTabIndex] = useState(() => parseInt(localStorage.getItem('platformAdminLaundryTab') || '0'));

    const headers = { 'x-platform-key': platformKey };

    const handleAuthError = () => {
        setIsAuthenticated(false);
        localStorage.removeItem('platformKey');
        setPlatformKey('');
        toast({ title: 'Invalid platform key', status: 'error' });
    };

    const handleTabChange = (index) => {
        setTabIndex(index);
        localStorage.setItem('platformAdminTab', String(index));
    };

    // Auth check
    const handleAuth = () => {
        const key = platformKey.trim();
        setPlatformKey(key);
        localStorage.setItem('platformKey', key);
        setIsAuthenticated(true);
        fetchLaundries(key);
        fetchInvoiceStatus(key);
    };

    const fetchLaundries = async (key = platformKey) => {
        setLoading(true);
        try {
            const res = await axios.get(`${API_URL}/api/platform/laundries`, { headers: { 'x-platform-key': key } });
            if (res.data.status === 'success') setLaundries(res.data.laundries);
        } catch (err) {
            if (err.response?.status === 403) {
                handleAuthError();
            }
        } finally {
            setLoading(false);
        }
    };

    // Last month's platform invoice status per tenant (shown on each card).
    const [invoiceLaundry, setInvoiceLaundry] = useState(null);
    const [invoiceStatus, setInvoiceStatus] = useState({ month: previousMonth(), billingLaundryId: null, byLaundry: {} });
    const fetchInvoiceStatus = async (key = platformKey) => {
        try {
            const res = await axios.get(`${API_URL}/api/platform/invoices`, { headers: { 'x-platform-key': key } });
            const byLaundry = {};
            (res.data.invoices || []).forEach((inv) => { byLaundry[inv.laundryId] = inv; });
            setInvoiceStatus({ month: res.data.month, billingLaundryId: res.data.billingLaundryId, byLaundry });
        } catch (_) { /* cards just won't show invoice status */ }
    };

    useEffect(() => {
        if (platformKey) { setIsAuthenticated(true); fetchLaundries(); fetchInvoiceStatus(); }
    }, []);

    // Create laundry state
    const [newLaundry, setNewLaundry] = useState({
        laundryName: '', timezone: 'America/Chicago', street: '', city: '', state: '', zipCode: '',
        bagPrice: 30, ownerFirstName: '', ownerLastName: '', ownerEmail: '', ownerPhone: '',
        stripePublicKey: '', stripePrivateKey: '',
    });
    const [creating, setCreating] = useState(false);
    const [createResult, setCreateResult] = useState(null);

    const handleCreate = async () => {
        setCreating(true);
        try {
            const res = await axios.post(`${API_URL}/api/platform/laundries`, newLaundry, { headers });
            if (res.data.status === 'success') {
                setCreateResult(res.data);
                fetchLaundries();
                toast({ title: 'Laundry created!', status: 'success' });
            } else {
                toast({ title: res.data.message, status: 'error' });
            }
        } catch (err) {
            toast({ title: err.response?.data?.message || 'Error', status: 'error' });
        } finally {
            setCreating(false);
        }
    };

    const resetRegCode = async (laundryId) => {
        try {
            const res = await axios.post(`${API_URL}/api/platform/laundries/${laundryId}/reset-registration-code`, {}, { headers });
            if (res.data.status === 'success') {
                toast({ title: `New code: ${res.data.newCode}`, status: 'success', duration: 10000 });
                fetchLaundries();
            }
        } catch (err) {
            toast({ title: 'Error', status: 'error' });
        }
    };

    const setSmsNumber = async (laundry) => {
        const entered = window.prompt(
            `Texting number for ${laundry.laundryName} (must already be in the platform's Twilio account). Leave blank to use the shared number.`,
            laundry.smsNumber || ''
        );
        if (entered === null) return;
        try {
            const res = await axios.put(`${API_URL}/api/platform/laundries/${laundry.laundryId}/sms-number`,
                { smsNumber: entered }, { headers });
            const ok = res.data.status === 'success';
            toast({ title: ok ? 'Texting number saved' : 'Not saved', description: res.data.message,
                status: ok ? 'success' : 'error', duration: 8000, isClosable: true });
            if (ok) {
                setLaundries(prev => prev.map(x => x.laundryId === laundry.laundryId ? { ...x, smsNumber: res.data.smsNumber } : x));
            }
        } catch (err) {
            toast({ title: 'Error', description: err.response?.data?.detail || err.message, status: 'error' });
        }
    };

    // Send owner credentials state
    const [sendingCredentials, setSendingCredentials] = useState({});
    const [credentialsModal, setCredentialsModal] = useState(null); // { laundryId, employees, regCode }

    const [sendingEnrollment, setSendingEnrollment] = useState({});
    const [tenantMailOpen, setTenantMailOpen] = useState(false);

    const sendEnrollmentEmail = async (laundry) => {
        const ok = window.confirm(
            `Send the enrollment email to the owner of "${laundry.laundryName}"? It includes their username, passcode, and the setup links.`
        );
        if (!ok) return;
        setSendingEnrollment(prev => ({ ...prev, [laundry.laundryId]: true }));
        try {
            const res = await axios.post(
                `${API_URL}/api/platform/laundries/${laundry.laundryId}/send-enrollment-email`,
                {},
                { headers }
            );
            if (res.data.status === 'success') {
                toast({ title: 'Enrollment email sent', description: res.data.message, status: 'success', duration: 6000 });
            } else {
                toast({ title: 'Not sent', description: res.data.message, status: 'error', duration: 6000 });
            }
        } catch (err) {
            toast({ title: 'Error', description: err.response?.data?.message || err.response?.data?.detail || 'Failed to send', status: 'error' });
        } finally {
            setSendingEnrollment(prev => ({ ...prev, [laundry.laundryId]: false }));
        }
    };

    const sendOwnerCredentials = async (laundryId, empId) => {
        setSendingCredentials(prev => ({ ...prev, [laundryId + (empId || '')]: true }));
        try {
            const res = await axios.post(
                `${API_URL}/api/platform/laundries/${laundryId}/send-owner-credentials`,
                empId ? { empId } : {},
                { headers }
            );
            if (res.data.status === 'success') {
                toast({ title: 'Credentials Sent', description: res.data.message, status: 'success', duration: 5000 });
            } else {
                toast({ title: 'Failed', description: res.data.message, status: 'error', duration: 5000 });
            }
        } catch (err) {
            toast({ title: 'Error', description: err.response?.data?.message || 'Failed to send', status: 'error' });
        } finally {
            setSendingCredentials(prev => ({ ...prev, [laundryId + (empId || '')]: false }));
        }
    };

    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteTyped, setDeleteTyped] = useState('');
    const [deleting, setDeleting] = useState(false);

    const handleDeleteLaundry = async () => {
        if (!deleteTarget) return;
        const { laundryId, laundryName } = deleteTarget;
        if (deleteTyped.trim() !== (laundryName || '').trim()) return;
        setDeleting(true);
        try {
            const res = await axios.delete(`${API_URL}/api/platform/laundries/${laundryId}`, {
                headers,
                params: { confirmName: deleteTyped.trim() },
            });
            if (res.data.status === 'success') {
                toast({ title: `"${laundryName}" deleted`, status: 'success', duration: 3000 });
                setLaundries(prev => prev.filter(l => l.laundryId !== laundryId));
                setDeleteTarget(null);
                setDeleteTyped('');
            } else {
                toast({ title: 'Not deleted', description: res.data.message, status: 'error', duration: 4000 });
            }
        } catch (err) {
            toast({ title: 'Delete failed', description: err.response?.data?.message || err.message, status: 'error', duration: 4000 });
        } finally {
            setDeleting(false);
        }
    };

    const viewOwnerCredentials = async (laundryId) => {
        try {
            const res = await axios.get(`${API_URL}/api/platform/laundries/${laundryId}/owner-credentials`, { headers });
            if (res.data.status === 'success') {
                setCredentialsModal({
                    laundryId,
                    employees: res.data.employees,
                    regCode: res.data.deviceRegistrationCode,
                });
            }
        } catch (err) {
            toast({ title: 'Error', description: 'Failed to retrieve credentials', status: 'error' });
        }
    };

    // Support Chat State
    const [chatOpen, setChatOpen] = useState(false);
    const [chatLaundry, setChatLaundry] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [chatSending, setChatSending] = useState(false);
    const [unreadCounts, setUnreadCounts] = useState({});
    const chatEndRef = useRef(null);
    const chatPollRef = useRef(null);
    const chatInputRef = useRef(null);
    const chatBoxRef = useRef(null);

    // Prospect Chat State
    const [prospectConversations, setProspectConversations] = useState([]);
    const [prospectChatOpen, setProspectChatOpen] = useState(false);
    const [prospectChatConv, setProspectChatConv] = useState(null);
    const [prospectMessages, setProspectMessages] = useState([]);
    const [prospectSending, setProspectSending] = useState(false);
    const prospectEndRef = useRef(null);
    const prospectPollRef = useRef(null);
    const prospectInputRef = useRef(null);
    const prospectBoxRef = useRef(null);

    // Fetch prospect conversations (from product page visitors)
    const fetchProspectConversations = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/chat/admin/conversations`, {
                params: { laundryId: 'platform' },
                headers: { ...headers, Authorization: `Bearer ${platformKey}` }
            });
            if (!res.data?.conversations) return;
            const counts = {};
            const prospects = [];
            res.data.conversations.forEach(c => {
                if (c.customerId?.startsWith('laundry-')) {
                    const lid = c.customerId.replace('laundry-', '');
                    if (lid) counts[lid] = c.unreadCount || 0;
                } else {
                    prospects.push({ ...c, unreadAdmin: c.unreadCount || 0 });
                }
            });
            setUnreadCounts(counts);
            setProspectConversations(prospects);
        } catch (err) { /* ok */ }
    };

    const openProspectChat = (conv) => {
        setProspectChatConv(conv);
        setProspectChatOpen(true);
        fetchProspectMessages(conv.conversationId);
        if (prospectPollRef.current) clearInterval(prospectPollRef.current);
        prospectPollRef.current = setInterval(() => fetchProspectMessages(conv.conversationId), 5000);
    };

    const closeProspectChat = () => {
        setProspectChatOpen(false);
        if (prospectPollRef.current) clearInterval(prospectPollRef.current);
        fetchProspectConversations(); // Refresh to update unread counts
    };

    const fetchProspectMessages = async (conversationId) => {
        try {
            const res = await axios.get(`${API_URL}/api/chat/admin/messages`, {
                params: { conversationId, laundryId: 'platform' },
                headers: { ...headers, Authorization: `Bearer ${platformKey}` }
            });
            if (res.data?.messages) {
                setProspectMessages(prev => mergeServerMessages(prev, res.data.messages));
            }
        } catch (err) { /* ok */ }
    };

    const sendProspectReply = async (text) => {
        if (!text || !prospectChatConv || prospectSending) return false;
        const pendingId = `local-${Date.now()}`;
        setProspectMessages(prev => [...prev, {
            messageId: pendingId,
            senderType: 'admin',
            senderName: 'Smart Laundry Basket',
            message: text,
            createdAt: new Date().toISOString(),
        }]);
        setProspectSending(true);
        try {
            await axios.post(`${API_URL}/api/chat/admin/send`, {
                conversationId: prospectChatConv.conversationId,
                message: text,
                senderName: 'Smart Laundry Basket',
            }, { headers: { ...headers, Authorization: `Bearer ${platformKey}` } });
            fetchProspectMessages(prospectChatConv.conversationId);
            return true;
        } catch (err) {
            setProspectMessages(prev => prev.filter(m => m.messageId !== pendingId));
            toast({ title: 'Send failed', status: 'error', duration: 2000 });
            return false;
        } finally {
            setProspectSending(false);
        }
    };

    useEffect(() => {
        const box = prospectBoxRef.current;
        if (box) box.scrollTop = box.scrollHeight;
    }, [prospectMessages]);

    useEffect(() => {
        if (!isAuthenticated) return;
        fetchProspectConversations();
        const interval = setInterval(fetchProspectConversations, 30000);
        return () => clearInterval(interval);
    }, [isAuthenticated]);

    const openChat = (laundry) => {
        setChatLaundry(laundry);
        setChatOpen(true);
        fetchChatMessages(laundry.laundryId);
        // Reset unread count locally
        setUnreadCounts(prev => ({ ...prev, [laundry.laundryId]: 0 }));
        if (chatPollRef.current) clearInterval(chatPollRef.current);
        chatPollRef.current = setInterval(() => fetchChatMessages(laundry.laundryId), 5000);
    };

    const closeChat = () => {
        setChatOpen(false);
        if (chatPollRef.current) clearInterval(chatPollRef.current);
    };

    const fetchChatMessages = async (lid) => {
        try {
            // First get the conversation to find conversationId
            const res = await axios.get(`${API_URL}/api/chat/messages`, {
                params: { customerId: `laundry-${lid}`, laundryId: 'platform' }
            });
            if (res.data?.messages) {
                setChatMessages(prev => mergeServerMessages(prev, res.data.messages));
            }
            // If conversation exists, also call admin/messages to mark as read
            if (res.data?.conversationId) {
                await axios.get(`${API_URL}/api/chat/admin/messages`, {
                    params: { conversationId: res.data.conversationId, laundryId: 'platform' },
                    headers: { Authorization: `Bearer ${platformKey}` }
                }).catch(() => {});
            }
        } catch (err) { /* ok */ }
    };

    const sendChatMessage = async (text) => {
        if (!text || !chatLaundry || chatSending) return false;
        const pendingId = `local-${Date.now()}`;
        setChatMessages(prev => [...prev, {
            messageId: pendingId,
            senderType: 'admin',
            senderName: 'Platform Support',
            message: text,
            createdAt: new Date().toISOString(),
        }]);
        setChatSending(true);
        try {
            const lid = chatLaundry.laundryId;
            // Check if conversation exists
            const msgRes = await axios.get(`${API_URL}/api/chat/messages`, {
                params: { customerId: `laundry-${lid}`, laundryId: 'platform' }
            });
            let convId = msgRes.data?.conversationId;

            if (!convId) {
                // Create conversation by sending a dummy init as "customer" then immediately reply as admin
                const initRes = await axios.post(`${API_URL}/api/chat/send`, {
                    customerId: `laundry-${lid}`,
                    laundryId: 'platform',
                    message: '(Support conversation started)',
                    customerName: chatLaundry.laundryName || `Laundry ${lid}`,
                    customerPhone: '',
                });
                convId = initRes.data?.conversationId;
            }

            if (convId) {
                // Send as admin
                await axios.post(`${API_URL}/api/chat/admin/send`, {
                    conversationId: convId,
                    message: text,
                    senderName: 'Platform Support',
                }, { headers: { Authorization: `Bearer ${platformKey}` } });
            }

            fetchChatMessages(lid);
            return true;
        } catch (err) {
            console.error(err);
            setChatMessages(prev => prev.filter(m => m.messageId !== pendingId));
            toast({ title: 'Send failed', status: 'error', duration: 2000 });
            return false;
        }
        finally { setChatSending(false); }
    };

    useEffect(() => {
        const box = chatBoxRef.current;
        if (box) box.scrollTop = box.scrollHeight;
    }, [chatMessages]);

    const liveLaundries = laundries.filter(l => l.hasStripe);
    const testLaundries = laundries.filter(l => !l.hasStripe);

    if (!isAuthenticated) {
        return (
            <Box minH="100vh" bg="gray.900" display="flex" alignItems="center" justifyContent="center" p={4}>
                <Box bg="white" p={8} borderRadius="2xl" maxW="400px" w="full" boxShadow="2xl">
                    <VStack spacing={5}>
                        <Heading size="md" color="gray.800">Platform Admin</Heading>
                        <Text fontSize="sm" color="gray.500">Enter your platform admin key to continue.</Text>
                        <FormControl>
                            <Input
                                type="password"
                                placeholder="Platform admin key"
                                value={platformKey}
                                onChange={(e) => setPlatformKey(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && handleAuth()}
                            />
                        </FormControl>
                        <Button colorScheme="blue" w="full" onClick={handleAuth}>Access</Button>
                    </VStack>
                </Box>
            </Box>
        );
    }

    return (
        <Box minH="100vh" bg="gray.50" p={{ base: 4, md: 8 }}>
            <Flex justify="space-between" align="center" mb={6}>
                <Heading size="lg" color="gray.800">Platform Admin</Heading>
            </Flex>

            <Tabs index={tabIndex} onChange={handleTabChange} colorScheme="blue" isLazy lazyBehavior="keepMounted">
                <TabList mb={4}>
                    <Tab>Laundries</Tab>
                    <Tab>Companies</Tab>
                    <Tab>
                        <HStack spacing={1}>
                            <FiMessageCircle />
                            <Text>Prospect Chats</Text>
                            {prospectConversations.length > 0 && prospectConversations.some(c => c.unreadAdmin > 0) && (
                                <Badge colorScheme="red" borderRadius="full" fontSize="xs" ml={1}>
                                    {prospectConversations.reduce((sum, c) => sum + (c.unreadAdmin || 0), 0)}
                                </Badge>
                            )}
                        </HStack>
                    </Tab>
                    <Tab>SMS Usage</Tab>
                    <Tab>Chat Usage</Tab>
                    <Tab>AI Photos</Tab>
                    <Tab>Customers</Tab>
                    <Tab>Directory</Tab>
                    <Tab>Brand Insights</Tab>
                </TabList>
                <TabPanels>
                    <TabPanel p={0}>
                        <Flex justify="flex-end" mb={4}>
                            <HStack>
                                <IconButton icon={<FiRefreshCw />} onClick={() => fetchLaundries()} size="sm" variant="ghost" />
                                <Button as="a" href={OPERATORS_FB_GROUP} target="_blank" rel="noopener noreferrer" colorScheme="blue" size="sm" variant="outline">
                                    Operators group
                                </Button>
                                <Button leftIcon={<FiMail />} colorScheme="orange" size="sm" variant="outline" onClick={() => setTenantMailOpen(true)}>
                                    Email all tenants
                                </Button>
                                <Button leftIcon={<FiPlus />} colorScheme="blue" size="sm" onClick={onOpen}>
                                    New Laundry
                                </Button>
                            </HStack>
                        </Flex>

                        {loading ? (
                            <Flex justify="center" py={10}><Spinner size="xl" /></Flex>
                        ) : (
                            <Tabs index={laundryTabIndex} variant="soft-rounded" colorScheme="green" size="sm"
                                onChange={(i) => { setLaundryTabIndex(i); localStorage.setItem('platformAdminLaundryTab', String(i)); }}>
                                <TabList mb={4}>
                                    <Tab>Live Clients (Stripe) · {liveLaundries.length}</Tab>
                                    <Tab>Test / No Stripe · {testLaundries.length}</Tab>
                                </TabList>
                                <TabPanels>
                                    {[liveLaundries, testLaundries].map((group, gi) => (
                                    <TabPanel p={0} key={gi}>
                                    {group.length === 0 ? (
                                        <Text color="gray.400" textAlign="center" py={10}>No laundries here.</Text>
                                    ) : (
                            <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                                {[...group].sort((a, b) => {
                                    const aUnder = (a.monthlyRevenue || 0) < 1000 ? 0 : 1;
                                    const bUnder = (b.monthlyRevenue || 0) < 1000 ? 0 : 1;
                                    if (aUnder !== bUnder) return aUnder - bUnder;
                                    if (aUnder === 0) {
                                        const byStops = (a.weeklyStops || 0) - (b.weeklyStops || 0);
                                        if (byStops) return byStops;
                                        return (a.monthlyRevenue || 0) - (b.monthlyRevenue || 0);
                                    }
                                    return 0;
                                }).map((l) => (
                        <Box key={l.laundryId} bg="white" borderRadius="xl" p={5} boxShadow="sm" border="1px solid" borderColor="gray.100">
                            <VStack align="stretch" spacing={3}>
                                <Flex justify="space-between" align="center">
                                    <Text fontWeight="700" fontSize="md">{l.laundryName}</Text>
                                    <Badge colorScheme="blue" fontSize="xs">ID: {l.laundryId}</Badge>
                                </Flex>
                                <Text fontSize="xs" color="gray.500">{l.address}</Text>
                                {l.ownerName && (
                                    <HStack fontSize="xs" color="gray.600" spacing={3}>
                                        <Text fontWeight="600">👤 {l.ownerName}</Text>
                                        {l.ownerEmail && <Text>{l.ownerEmail}</Text>}
                                        {l.ownerPhone && <Text>{l.ownerPhone}</Text>}
                                    </HStack>
                                )}
                                <Divider />
                                <HStack justify="space-between" fontSize="xs" color="gray.600">
                                    <HStack><FiUsers /><Text>{l.employeeCount} employees</Text></HStack>
                                    <Text>{l.activeOrders} active orders</Text>
                                </HStack>
                                <HStack justify="space-between" fontSize="xs">
                                    <Text fontWeight="600" color={l.monthlyRevenue >= 3000 ? 'green.600' : 'gray.600'}>
                                        💰 This Month: ${(l.monthlyRevenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                                    </Text>
                                    {(l.monthlyRevenue || 0) < 1000 && (
                                        <Badge colorScheme="orange" fontSize="xs">{l.weeklyStops || 0} of 5 stops</Badge>
                                    )}
                                    {l.monthlyRevenue >= 3000 && (
                                        <Badge colorScheme="green" fontSize="xs">Billable</Badge>
                                    )}
                                </HStack>
                                {/* Order vs subscription split — shown only when the tenant has
                                    subscription revenue, so billing reflects both sources. */}
                                {(l.subscriptionRevenue || 0) > 0 && (
                                    <HStack justify="space-between" fontSize="xs" color="gray.500" pl={4}>
                                        <Text>Orders: ${(l.orderRevenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</Text>
                                        <Text color="purple.500">Subscriptions: ${(l.subscriptionRevenue || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</Text>
                                    </HStack>
                                )}
                                <HStack justify="space-between" fontSize="xs">
                                    <HStack><FiKey /><Text color="orange.500">Code: {l.deviceRegistrationCode}</Text></HStack>
                                    <Button size="xs" variant="ghost" onClick={() => resetRegCode(l.laundryId)}>Reset</Button>
                                </HStack>
                                <HStack fontSize="xs" color="gray.500">
                                    <Badge colorScheme={l.hasStripe ? 'green' : 'red'} fontSize="xs">
                                        Stripe {l.hasStripe ? '✓' : '✗'}
                                    </Badge>
                                    <Badge colorScheme={l.hasTerminal ? 'green' : 'gray'} fontSize="xs">
                                        Terminal {l.hasTerminal ? '✓' : '—'}
                                    </Badge>
                                    <Text>${l.bagPrice}/bag</Text>
                                </HStack>
                                <HStack justify="space-between" fontSize="xs">
                                    <HStack>
                                        <FiMessageCircle />
                                        <Text color={l.smsNumber ? 'green.600' : 'gray.500'}>
                                            Texting: {l.smsNumber || 'shared number'}
                                        </Text>
                                    </HStack>
                                    <Button size="xs" variant="ghost" onClick={() => setSmsNumber(l)}>
                                        {l.smsNumber ? 'Change' : 'Set own'}
                                    </Button>
                                </HStack>
                                {String(l.laundryId) !== String(invoiceStatus.billingLaundryId) && (() => {
                                    const inv = invoiceStatus.byLaundry[l.laundryId];
                                    return (
                                        <HStack w="100%" spacing={2}>
                                            <Button size="xs" leftIcon={<FiFileText />} colorScheme="green" variant="outline"
                                                onClick={() => setInvoiceLaundry(l)} flex={1}>
                                                Invoice
                                            </Button>
                                            {inv && (
                                                <Badge fontSize="xs" colorScheme={inv.status === 'sent' ? 'green' : inv.status === 'failed' ? 'red' : 'yellow'}>
                                                    {invoiceStatus.month}: {inv.status === 'sent' ? `sent $${Number(inv.total || 0).toFixed(2)}` : inv.status}
                                                </Badge>
                                            )}
                                        </HStack>
                                    );
                                })()}
                                <Button size="xs" leftIcon={<FiMessageCircle />} colorScheme="cyan" variant="outline" onClick={() => openChat(l)} w="100%" position="relative">
                                    Chat with Owner
                                    {unreadCounts[l.laundryId] > 0 && (
                                        <Badge colorScheme="red" borderRadius="full" position="absolute" top="-6px" right="-6px" fontSize="xs" minW="18px" textAlign="center">
                                            {unreadCounts[l.laundryId]}
                                        </Badge>
                                    )}
                                </Button>
                                <HStack w="100%" spacing={2}>
                                    <Button size="xs" leftIcon={<FiEye />} colorScheme="purple" variant="outline" onClick={() => viewOwnerCredentials(l.laundryId)} flex={1}>
                                        View Credentials
                                    </Button>
                                    <Button size="xs" leftIcon={<FiMail />} colorScheme="orange" variant="outline"
                                        onClick={() => sendOwnerCredentials(l.laundryId)}
                                        isLoading={sendingCredentials[l.laundryId + '']}
                                        flex={1}>
                                        Send Credentials
                                    </Button>
                                </HStack>
                                <Button size="xs" leftIcon={<FiMail />} colorScheme="blue" variant="solid" w="100%"
                                    onClick={() => sendEnrollmentEmail(l)}
                                    isLoading={sendingEnrollment[l.laundryId]}>
                                    Send enrollment email
                                </Button>
                                <Text
                                    as="button"
                                    type="button"
                                    fontSize="xs"
                                    color="gray.400"
                                    textAlign="left"
                                    _hover={{ color: 'red.400' }}
                                    onClick={() => { setDeleteTarget(l); setDeleteTyped(''); }}
                                >
                                    Remove this WDF POS
                                </Text>
                            </VStack>
                        </Box>
                    ))}
                </SimpleGrid>
                                    )}
                                    </TabPanel>
                                    ))}
                                </TabPanels>
                            </Tabs>
            )}
                    </TabPanel>
                    <TabPanel p={0}>
                        <CompanyManagementSection platformKey={platformKey} onAuthError={handleAuthError} />
                    </TabPanel>
                    <TabPanel p={0}>
                        {/* Prospect Chats — messages from product page visitors */}
                        <VStack spacing={4} align="stretch">
                            <Flex justify="space-between" align="center">
                                <Text fontWeight="600" color="gray.700">Website visitor conversations (from smartlaundrybasket.ai chat)</Text>
                                <IconButton icon={<FiRefreshCw />} size="sm" variant="ghost" onClick={fetchProspectConversations} />
                            </Flex>
                            {prospectConversations.length === 0 ? (
                                <Box textAlign="center" py={10} bg="white" borderRadius="xl" border="1px solid" borderColor="gray.100">
                                    <Text color="gray.400">No prospect conversations yet.</Text>
                                    <Text fontSize="xs" color="gray.400" mt={1}>When visitors escalate from AI chat on the product page, their conversations appear here.</Text>
                                </Box>
                            ) : (
                                <VStack spacing={3} align="stretch">
                                    {prospectConversations.map(conv => (
                                        <Box key={conv.conversationId} bg="white" p={4} borderRadius="xl" border="1px solid"
                                            borderColor={conv.unreadAdmin > 0 ? 'blue.200' : 'gray.100'}
                                            cursor="pointer" _hover={{ boxShadow: 'md' }}
                                            onClick={() => openProspectChat(conv)}>
                                            <Flex justify="space-between" align="center">
                                                <HStack>
                                                    <FiMessageCircle color={conv.unreadAdmin > 0 ? '#3182CE' : '#A0AEC0'} />
                                                    <Text fontWeight="600" fontSize="sm">{conv.customerName || 'Website Visitor'}</Text>
                                                    {conv.unreadAdmin > 0 && (
                                                        <Badge colorScheme="blue" borderRadius="full" fontSize="xs">{conv.unreadAdmin} new</Badge>
                                                    )}
                                                </HStack>
                                                <Text fontSize="xs" color="gray.500">
                                                    {formatCentral(conv.lastMessageAt, true)}
                                                </Text>
                                            </Flex>
                                        </Box>
                                    ))}
                                </VStack>
                            )}
                        </VStack>
                    </TabPanel>
                    <TabPanel p={0}>
                        <SmsUsageSection platformKey={platformKey} onAuthError={handleAuthError} />
                    </TabPanel>
                    <TabPanel p={0}>
                        <ChatUsageSection platformKey={platformKey} onAuthError={handleAuthError} />
                    </TabPanel>
                    <TabPanel p={0}>
                        <VisionUsageSection platformKey={platformKey} onAuthError={handleAuthError} />
                    </TabPanel>
                    <TabPanel p={0}>
                        <CustomerPhoneSection platformKey={platformKey} onAuthError={handleAuthError} />
                    </TabPanel>
                    <TabPanel p={0}>
                        <DirectoryOutsideSection platformKey={platformKey} onAuthError={handleAuthError} />
                    </TabPanel>
                    <TabPanel p={0}>
                        <BrandInsightsSection platformKey={platformKey} onAuthError={handleAuthError} />
                    </TabPanel>
                </TabPanels>
            </Tabs>

            <PlatformInvoiceModal
                laundry={invoiceLaundry}
                isOpen={!!invoiceLaundry}
                onClose={() => setInvoiceLaundry(null)}
                platformKey={platformKey}
                onSent={() => fetchInvoiceStatus()}
            />

            <TenantMailModal isOpen={tenantMailOpen} onClose={() => setTenantMailOpen(false)} platformKey={platformKey} />

            {/* Create Laundry Modal */}
            <Modal isOpen={isOpen} onClose={() => { onClose(); setCreateResult(null); }} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Onboard New Laundry</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {createResult ? (
                            <VStack spacing={4} align="stretch" bg="green.50" p={4} borderRadius="lg">
                                <Text fontWeight="700" color="green.700">✅ Laundry Created Successfully!</Text>
                                <Divider />
                                <Text fontSize="sm"><strong>Laundry ID:</strong> {createResult.laundry.laundryId}</Text>
                                <Text fontSize="sm"><strong>Admin URL:</strong> {createResult.laundry.adminUrl}</Text>
                                <Text fontSize="sm"><strong>Customer URL:</strong> {createResult.laundry.customerUrl}</Text>
                                <Text fontSize="sm"><strong>Device Reg Code:</strong> {createResult.laundry.deviceRegistrationCode}</Text>
                                <Divider />
                                <Text fontSize="sm" fontWeight="600">Owner Credentials:</Text>
                                <Text fontSize="sm"><strong>Employee ID:</strong> {createResult.owner.employeeId}</Text>
                                <Text fontSize="sm"><strong>Passcode:</strong> {createResult.owner.passcode}</Text>
                                <Text fontSize="xs" color="red.500" mt={2}>
                                    ⚠️ Save these credentials! The passcode cannot be retrieved later.
                                </Text>
                            </VStack>
                        ) : (
                            <VStack spacing={4} align="stretch">
                                <FormControl isRequired>
                                    <FormLabel fontSize="sm">Laundry Name</FormLabel>
                                    <Input value={newLaundry.laundryName} onChange={(e) => setNewLaundry({ ...newLaundry, laundryName: e.target.value })} />
                                </FormControl>
                                <HStack>
                                    <FormControl><FormLabel fontSize="sm">Street</FormLabel>
                                        <Input value={newLaundry.street} onChange={(e) => setNewLaundry({ ...newLaundry, street: e.target.value })} />
                                    </FormControl>
                                    <FormControl><FormLabel fontSize="sm">City</FormLabel>
                                        <Input value={newLaundry.city} onChange={(e) => setNewLaundry({ ...newLaundry, city: e.target.value })} />
                                    </FormControl>
                                </HStack>
                                <HStack>
                                    <FormControl><FormLabel fontSize="sm">State</FormLabel>
                                        <Input value={newLaundry.state} onChange={(e) => setNewLaundry({ ...newLaundry, state: e.target.value })} />
                                    </FormControl>
                                    <FormControl><FormLabel fontSize="sm">Zip Code</FormLabel>
                                        <Input value={newLaundry.zipCode} onChange={(e) => setNewLaundry({ ...newLaundry, zipCode: e.target.value })} />
                                    </FormControl>
                                    <FormControl><FormLabel fontSize="sm">Bag Price ($)</FormLabel>
                                        <Input type="number" value={newLaundry.bagPrice} onChange={(e) => setNewLaundry({ ...newLaundry, bagPrice: e.target.value })} />
                                    </FormControl>
                                </HStack>
                                <Divider />
                                <Text fontWeight="600" fontSize="sm">Owner Details</Text>
                                <HStack>
                                    <FormControl isRequired><FormLabel fontSize="sm">First Name</FormLabel>
                                        <Input value={newLaundry.ownerFirstName} onChange={(e) => setNewLaundry({ ...newLaundry, ownerFirstName: e.target.value })} />
                                    </FormControl>
                                    <FormControl><FormLabel fontSize="sm">Last Name</FormLabel>
                                        <Input value={newLaundry.ownerLastName} onChange={(e) => setNewLaundry({ ...newLaundry, ownerLastName: e.target.value })} />
                                    </FormControl>
                                </HStack>
                                <HStack>
                                    <FormControl><FormLabel fontSize="sm">Email</FormLabel>
                                        <Input value={newLaundry.ownerEmail} onChange={(e) => setNewLaundry({ ...newLaundry, ownerEmail: e.target.value })} />
                                    </FormControl>
                                    <FormControl><FormLabel fontSize="sm">Phone</FormLabel>
                                        <Input value={newLaundry.ownerPhone} onChange={(e) => setNewLaundry({ ...newLaundry, ownerPhone: e.target.value })} />
                                    </FormControl>
                                </HStack>
                                <Divider />
                                <Text fontWeight="600" fontSize="sm">Stripe (optional — can add later)</Text>
                                <FormControl><FormLabel fontSize="sm">Stripe Public Key</FormLabel>
                                    <Input value={newLaundry.stripePublicKey} onChange={(e) => setNewLaundry({ ...newLaundry, stripePublicKey: e.target.value })} placeholder="pk_..." />
                                </FormControl>
                                <FormControl><FormLabel fontSize="sm">Stripe Secret Key</FormLabel>
                                    <Input type="password" value={newLaundry.stripePrivateKey} onChange={(e) => setNewLaundry({ ...newLaundry, stripePrivateKey: e.target.value })} placeholder="sk_..." />
                                </FormControl>
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        {!createResult && (
                            <Button colorScheme="blue" onClick={handleCreate} isLoading={creating} isDisabled={!newLaundry.laundryName || !newLaundry.ownerFirstName}>
                                Create Laundry
                            </Button>
                        )}
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Prospect Chat Drawer (website visitors) */}
            <Drawer isOpen={prospectChatOpen} onClose={closeProspectChat} placement="right" size="md"
                initialFocusRef={prospectInputRef} blockScrollOnMount={false}>
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader bg="blue.500" color="white" fontSize="md">
                        💬 {prospectChatConv?.customerName || 'Website Visitor'}
                    </DrawerHeader>
                    <DrawerBody display="flex" flexDirection="column" p={3} overflow="hidden" flex="1" minH="0">
                        <Box ref={prospectBoxRef} flex={1} overflowY="auto" bg="gray.50" borderRadius="md" p={3} mb={3} minH="0">
                            {prospectMessages.length === 0 ? (
                                <Text color="gray.400" textAlign="center" py={10} fontSize="sm">No messages yet.</Text>
                            ) : (
                                <VStack spacing={2} align="stretch">
                                    {prospectMessages.map(msg => (
                                        <Flex key={msg.messageId} justify={msg.senderType === 'admin' ? 'flex-end' : 'flex-start'}>
                                            <Box maxW="80%" bg={msg.senderType === 'admin' ? 'blue.500' : 'white'}
                                                color={msg.senderType === 'admin' ? 'white' : 'gray.800'}
                                                px={3} py={2} borderRadius="lg" boxShadow="sm"
                                                border={msg.senderType !== 'admin' ? '1px solid' : 'none'} borderColor="gray.200">
                                                <Text fontSize="sm">{msg.message}</Text>
                                                <Text fontSize="xs" opacity={0.7} mt={1}>
                                                    {msg.senderName || (msg.senderType === 'admin' ? 'You' : 'Visitor')} • {formatCentral(msg.createdAt)}
                                                </Text>
                                            </Box>
                                        </Flex>
                                    ))}
                                    <div ref={prospectEndRef} />
                                </VStack>
                            )}
                        </Box>
                        <ChatComposer
                            inputRef={prospectInputRef}
                            placeholder="Reply to visitor..."
                            colorScheme="blue"
                            onSend={sendProspectReply}
                            isSending={prospectSending}
                        />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>

            {/* Credentials Modal */}
            <Modal isOpen={!!credentialsModal} onClose={() => setCredentialsModal(null)} size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Owner Credentials</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {credentialsModal && (
                            <VStack spacing={4} align="stretch">
                                <Box bg="orange.50" p={3} borderRadius="md">
                                    <HStack><FiKey /><Text fontWeight="600" fontSize="sm">Device Registration Code:</Text></HStack>
                                    <Text fontSize="lg" fontWeight="bold" color="orange.600" mt={1}>{credentialsModal.regCode}</Text>
                                </Box>
                                <Divider />
                                {credentialsModal.employees.map(emp => (
                                    <Box key={emp.empId} bg="gray.50" p={3} borderRadius="md" border="1px solid" borderColor="gray.200">
                                        <HStack justify="space-between" mb={2}>
                                            <Text fontWeight="600" fontSize="sm">{emp.firstName} {emp.lastName}</Text>
                                            <Badge colorScheme={emp.role === 'Admin' ? 'red' : 'blue'} fontSize="xs">{emp.role}</Badge>
                                        </HStack>
                                        <Table size="sm" variant="simple">
                                            <Tbody>
                                                <Tr><Td fontWeight="600" px={2} py={1}>Employee ID</Td><Td px={2} py={1} fontFamily="mono">{emp.empId}</Td></Tr>
                                                <Tr><Td fontWeight="600" px={2} py={1}>Passcode</Td><Td px={2} py={1} fontFamily="mono" fontSize="lg" color="blue.600">{emp.passcode}</Td></Tr>
                                                <Tr><Td fontWeight="600" px={2} py={1}>Email</Td><Td px={2} py={1}>{emp.email || '—'}</Td></Tr>
                                            </Tbody>
                                        </Table>
                                        {emp.email && (
                                            <Button size="xs" leftIcon={<FiMail />} colorScheme="orange" mt={2}
                                                onClick={() => sendOwnerCredentials(credentialsModal.laundryId, emp.empId)}
                                                isLoading={sendingCredentials[credentialsModal.laundryId + emp.empId]}>
                                                Email Credentials to {emp.firstName}
                                            </Button>
                                        )}
                                    </Box>
                                ))}
                                {credentialsModal.employees.length === 0 && (
                                    <Text color="gray.500" textAlign="center" py={4}>No admin employees found.</Text>
                                )}
                            </VStack>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={() => setCredentialsModal(null)}>Close</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={!!deleteTarget} onClose={() => { if (!deleting) { setDeleteTarget(null); setDeleteTyped(''); } }} size="md">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>Remove this WDF POS</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack align="stretch" spacing={3}>
                            <Text fontSize="sm">
                                This deletes <strong>{deleteTarget?.laundryName}</strong> and all of its orders, customers, and settings. It cannot be undone.
                            </Text>
                            <FormControl>
                                <FormLabel fontSize="sm">Type the name to confirm</FormLabel>
                                <Input
                                    value={deleteTyped}
                                    placeholder={deleteTarget?.laundryName || ''}
                                    onChange={(e) => setDeleteTyped(e.target.value)}
                                    autoComplete="off"
                                />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button mr={3} variant="ghost" onClick={() => { setDeleteTarget(null); setDeleteTyped(''); }} isDisabled={deleting}>
                            Cancel
                        </Button>
                        <Button
                            colorScheme="red"
                            isDisabled={!deleteTarget || deleteTyped.trim() !== (deleteTarget.laundryName || '').trim()}
                            isLoading={deleting}
                            onClick={handleDeleteLaundry}
                        >
                            Delete permanently
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Support Chat Drawer (tenant owners) */}
            <Drawer isOpen={chatOpen} onClose={closeChat} placement="right" size="md"
                initialFocusRef={chatInputRef} blockScrollOnMount={false}>
                <DrawerOverlay />
                <DrawerContent>
                    <DrawerCloseButton />
                    <DrawerHeader bg="cyan.500" color="white" fontSize="md">
                        💬 Chat with {chatLaundry?.laundryName || 'Tenant'}
                    </DrawerHeader>
                    <DrawerBody display="flex" flexDirection="column" p={3} overflow="hidden" flex="1" minH="0">
                        <Box ref={chatBoxRef} flex={1} overflowY="auto" bg="gray.50" borderRadius="md" p={3} mb={3} minH="0">
                            {chatMessages.length === 0 ? (
                                <Text color="gray.400" textAlign="center" py={10} fontSize="sm">No messages yet. Start the conversation.</Text>
                            ) : (
                                <VStack spacing={2} align="stretch">
                                    {chatMessages.map(msg => (
                                        <Flex key={msg.messageId} justify={msg.senderType === 'admin' ? 'flex-end' : 'flex-start'}>
                                            <Box maxW="80%" bg={msg.senderType === 'admin' ? 'cyan.500' : 'white'}
                                                color={msg.senderType === 'admin' ? 'white' : 'gray.800'}
                                                px={3} py={2} borderRadius="lg" boxShadow="sm"
                                                border={msg.senderType !== 'admin' ? '1px solid' : 'none'} borderColor="gray.200">
                                                <Text fontSize="sm">{msg.message}</Text>
                                                <Text fontSize="xs" opacity={0.7} mt={1}>
                                                    {msg.senderName} • {formatCentral(msg.createdAt, true)}
                                                </Text>
                                            </Box>
                                        </Flex>
                                    ))}
                                    <div ref={chatEndRef} />
                                </VStack>
                            )}
                        </Box>
                        <ChatComposer
                            inputRef={chatInputRef}
                            placeholder="Type a message..."
                            colorScheme="cyan"
                            onSend={sendChatMessage}
                            isSending={chatSending}
                        />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
        </Box>
    );
}
